import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { AccountsService } from '../../accounts.service';
import { AccountingPeriodLock } from '../../manual-entry-control.model';
import { SessionStorageService } from '../../../services/session-storage.service';

@Component({
    selector: 'app-accounting-period-locks',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, InputTextModule, SelectModule, TableModule, ToastModule, PageShellComponent, PageHeroComponent, PageFormSectionComponent, PageDataPanelComponent],
    providers: [MessageService],
    templateUrl: './accounting-period-locks.component.html',
    styleUrls: ['./accounting-period-locks.component.css']
})
export class AccountingPeriodLocksComponent implements OnInit {
    locks: AccountingPeriodLock[] = [];
    lockTypes = ['ACCOUNTING', 'TAX', 'ALL'];
    lockStatuses = [{ label: 'Locked', value: true }, { label: 'Unlocked', value: false }];
    startDate?: Date;
    endDate?: Date;
    draft: AccountingPeriodLock = {
        owningCompanyId: 0,
        lockType: 'ACCOUNTING',
        periodStartDate: '',
        periodEndDate: '',
        locked: true,
        reason: ''
    };

    constructor(private accountsService: AccountsService, private sessionStorage: SessionStorageService, private messageService: MessageService) {}

    ngOnInit(): void {
        this.draft.owningCompanyId = this.sessionStorage.getCompany()?.id || 0;
        this.load();
    }

    load(): void {
        if (!this.draft.owningCompanyId) return;
        this.accountsService.getAccountingPeriodLocks(this.draft.owningCompanyId).subscribe(locks => this.locks = locks);
    }

    save(): void {
        this.draft.periodStartDate = this.toIsoDate(this.startDate);
        this.draft.periodEndDate = this.toIsoDate(this.endDate);
        this.accountsService.saveAccountingPeriodLock(this.draft).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Accounting period lock saved.' });
                this.resetDraft();
                this.load();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save lock.' })
        });
    }

    edit(lock: AccountingPeriodLock): void {
        this.draft = { ...lock };
        this.startDate = lock.periodStartDate ? new Date(lock.periodStartDate) : undefined;
        this.endDate = lock.periodEndDate ? new Date(lock.periodEndDate) : undefined;
    }

    resetDraft(): void {
        this.startDate = undefined;
        this.endDate = undefined;
        this.draft = {
            owningCompanyId: this.sessionStorage.getCompany()?.id || 0,
            lockType: 'ACCOUNTING',
            periodStartDate: '',
            periodEndDate: '',
            locked: true,
            reason: ''
        };
    }

    unlock(lock: AccountingPeriodLock): void {
        const reason = window.prompt('Unlock reason');
        if (!lock.periodLockId || !reason?.trim()) return;
        this.accountsService.unlockAccountingPeriodLock(lock.periodLockId, reason.trim()).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Unlocked', detail: 'Period lock released.' });
                this.load();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Unlock failed', detail: err?.error?.message || 'Could not unlock period.' })
        });
    }

    private toIsoDate(date?: Date): string {
        return date ? date.toISOString().slice(0, 10) : '';
    }
}

